import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Play, Calendar, Utensils, TrendingUp, Trophy, Flame, MessageSquare,
  ChevronRight, Dumbbell, Clock, Target, Bell, User, LogOut, Settings,
  Plus, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { fetchApi } from '../lib/utils';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await fetchApi('/dashboard');
      setDashboardData(data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { todays_workout, todays_nutrition, progress, challenges, unread_messages } = dashboardData || {};

  return (
    <div className="min-h-screen bg-black pb-24 md:pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
            data-testid="logo-home"
          >
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl tracking-tight hidden sm:block">VYTA</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <button className="text-white font-medium" data-testid="nav-dashboard">{t('dashboard')}</button>
            <button onClick={() => navigate('/training')} className="text-zinc-400 hover:text-white" data-testid="nav-training">{t('training')}</button>
            <button onClick={() => navigate('/nutrition')} className="text-zinc-400 hover:text-white" data-testid="nav-nutrition">{t('nutrition')}</button>
            <button onClick={() => navigate('/progress')} className="text-zinc-400 hover:text-white" data-testid="nav-progress">{t('progress')}</button>
            <button onClick={() => navigate('/challenges')} className="text-zinc-400 hover:text-white" data-testid="nav-challenges">{t('challenges')}</button>
          </nav>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/messages')}
              className="relative p-2 rounded-full hover:bg-zinc-800"
              data-testid="messages-btn"
            >
              <MessageSquare className="w-5 h-5 text-zinc-400" />
              {unread_messages > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-[#CCFF00] text-black text-xs flex items-center justify-center">
                  {unread_messages}
                </span>
              )}
            </button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-full hover:bg-zinc-800" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                    <User className="w-4 h-4 text-zinc-400" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-zinc-400">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  {t('settings_profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/subscription')} className="cursor-pointer">
                  <Target className="w-4 h-4 mr-2" />
                  {t('subscription')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-black mb-2">
            {t('welcome_back_name')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-zinc-400">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {/* Today's Workout - Large */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bento-card large card-interactive"
            onClick={() => navigate('/training')}
            data-testid="todays-workout-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-zinc-400 mb-1">{t('todays_workout')}</p>
                <h3 className="text-xl font-bold">{todays_workout?.focus || t('rest_day')}</h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-[#CCFF00]" />
              </div>
            </div>
            
            {todays_workout ? (
              <>
                <div className="flex items-center gap-4 mb-4 text-sm text-zinc-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {todays_workout.duration_minutes} {t('minutes')}
                  </span>
                  <span>{todays_workout.exercises?.length || 0} {t('exercises')}</span>
                </div>
                
                <div className="space-y-2 mb-4">
                  {todays_workout.exercises?.slice(0, 3).map((ex, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-zinc-300">{ex.name}</span>
                    </div>
                  ))}
                  {todays_workout.exercises?.length > 3 && (
                    <p className="text-xs text-zinc-500">+{todays_workout.exercises.length - 3} more</p>
                  )}
                </div>
                
                <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full" data-testid="start-workout-btn">
                  <Play className="w-4 h-4 mr-2" />
                  {t('start_workout')}
                </Button>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-zinc-500">{t('enjoy_rest_day')}</p>
              </div>
            )}
          </motion.div>

          {/* Streak */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bento-card"
            data-testid="streak-card"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-400">{t('current_streak')}</p>
              <Flame className="w-6 h-6 text-orange-500 streak-flame" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-black tabular-nums">{progress?.streak_days || 0}</span>
              <span className="text-zinc-400">{t('days')}</span>
            </div>
          </motion.div>

          {/* Workouts This Month */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bento-card"
            data-testid="monthly-workouts-card"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-400">{t('this_month')}</p>
              <Calendar className="w-5 h-5 text-[#14B8A6]" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-black tabular-nums">{progress?.workouts_this_month || 0}</span>
              <span className="text-zinc-400">{t('workouts')}</span>
            </div>
          </motion.div>

          {/* Nutrition - Large */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bento-card large card-interactive"
            onClick={() => navigate('/nutrition')}
            data-testid="nutrition-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-zinc-400 mb-1">{t('todays_nutrition')}</p>
                <h3 className="text-xl font-bold">{todays_nutrition?.total_calories || 0} kcal</h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center">
                <Utensils className="w-6 h-6 text-[#14B8A6]" />
              </div>
            </div>
            
            {todays_nutrition ? (
              <>
                <div className="space-y-3 mb-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">{t('protein')}</span>
                      <span>{todays_nutrition.total_protein}g</span>
                    </div>
                    <div className="macro-bar">
                      <div className="macro-bar-fill macro-protein" style={{ width: '70%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">{t('carbs')}</span>
                      <span>{todays_nutrition.total_carbs}g</span>
                    </div>
                    <div className="macro-bar">
                      <div className="macro-bar-fill macro-carbs" style={{ width: '60%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">{t('fat')}</span>
                      <span>{todays_nutrition.total_fat}g</span>
                    </div>
                    <div className="macro-bar">
                      <div className="macro-bar-fill macro-fat" style={{ width: '50%' }} />
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-zinc-500">
                  {todays_nutrition.meals?.length || 0} {t('meals_planned')}
                </p>
              </>
            ) : (
              <p className="text-zinc-500">{t('no_meal_plan')}</p>
            )}
          </motion.div>

          {/* Quick Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bento-card card-interactive"
            onClick={() => navigate('/progress')}
            data-testid="progress-card"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-400">{t('weight')}</p>
              <TrendingUp className="w-5 h-5 text-[#6366F1]" />
            </div>
            {progress?.latest_weight ? (
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black tabular-nums">{progress.latest_weight}</span>
                <span className="text-zinc-400">kg</span>
              </div>
            ) : (
              <Button variant="ghost" className="text-sm text-[#CCFF00]" data-testid="log-weight-btn">
                <Plus className="w-4 h-4 mr-1" />
                {t('log_weight')}
              </Button>
            )}
          </motion.div>

          {/* Challenges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bento-card card-interactive"
            onClick={() => navigate('/challenges')}
            data-testid="challenges-card"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-zinc-400">{t('active_challenges')}</p>
              <Trophy className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-4xl font-black tabular-nums">{challenges?.length || 0}</span>
              <span className="text-zinc-400">{t('active')}</span>
            </div>
            {challenges?.length > 0 && (
              <div className="space-y-2">
                {challenges.slice(0, 2).map((challenge, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300 truncate">{challenge.name}</span>
                    <span className="text-[#CCFF00]">{Math.round(challenge.user_progress || 0)}%</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <h2 className="text-lg font-bold mb-4">{t('quick_actions')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2 border-zinc-800 hover:bg-zinc-900"
              onClick={() => navigate('/progress')}
              data-testid="log-workout-btn"
            >
              <BarChart3 className="w-5 h-5 text-[#CCFF00]" />
              <span>{t('log_progress')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2 border-zinc-800 hover:bg-zinc-900"
              onClick={() => navigate('/training')}
              data-testid="view-program-btn"
            >
              <Calendar className="w-5 h-5 text-[#14B8A6]" />
              <span>{t('view_program')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2 border-zinc-800 hover:bg-zinc-900"
              onClick={() => navigate('/challenges')}
              data-testid="join-challenge-btn"
            >
              <Trophy className="w-5 h-5 text-[#F59E0B]" />
              <span>{t('challenges')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2 border-zinc-800 hover:bg-zinc-900"
              onClick={() => navigate('/messages')}
              data-testid="contact-btn"
            >
              <MessageSquare className="w-5 h-5 text-[#6366F1]" />
              <span>{t('contact')}</span>
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <div className="flex justify-around">
          {[
            { icon: Target, label: t('dashboard'), path: '/dashboard', active: true },
            { icon: Dumbbell, label: t('training'), path: '/training' },
            { icon: Utensils, label: t('nutrition'), path: '/nutrition' },
            { icon: TrendingUp, label: t('progress'), path: '/progress' },
            { icon: User, label: t('profile'), path: '/profile' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 p-2 ${item.active ? 'text-[#CCFF00]' : 'text-zinc-500'}`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
